<template>
  <div id="calendar" ref="elRoot" :class="componentClass.root" :style="styles">
    <div :class="componentClass.element('content')" :style="stylesContent">
      <div
        v-show="contentVisible"
        :class="componentClass.element('contentInner')"
      >
        <div
          id="generalEvents"
          ref="elGeneralEvents"
          :class="[
            componentClass.element('generalEvents'),
            { 'is-withContent': isGeneralEventsVisible },
          ]"
        >
          <slot name="generalEvents" />
        </div>

        <div
          id="calendarEvents"
          ref="elCalendarEvents"
          :class="[
            componentClass.element('calendarEvents'),
            { 'is-withContent': isCalendarEventsVisible },
          ]"
        >
          <slot name="calendarEvents" />
        </div>

        <div
          id="birthdays"
          ref="elBirthdays"
          :class="[
            componentClass.element('birthdays'),
            { 'is-withContent': isBirthdaysVisible },
          ]"
        >
          <slot name="birthdays" />
        </div>

        <div
          id="freelancers"
          ref="elFreelancers"
          :class="[
            componentClass.element('freelancers'),
            { 'is-withContent': isFreelancersVisible },
          ]"
        >
          <slot name="freelancers" />
        </div>

        <div
          id="employees"
          ref="elEmployees"
          :class="[
            componentClass.element('employees'),
            { 'is-withContent': isEmployeesVisible },
          ]"
        >
          <slot name="employees" />
        </div>

        <div
          id="vehicles"
          ref="elVehicles"
          :class="[
            componentClass.element('vehicles'),
            { 'is-withContent': isVehiclesVisible },
          ]"
        >
          <slot name="vehicles" />
        </div>

        <div id="jobs" ref="elJobs" :class="componentClass.element('jobs')">
          <slot name="jobs" />
        </div>

        <div
          id="afterJobs"
          ref="elAfterJobs"
          :class="componentClass.element('afterJobs')"
        >
          <slot name="afterJobs" />
        </div>
      </div>
    </div>

    <div v-if="$slots.settings" :class="componentClass.element('settings')">
      <slot name="settings" />
    </div>

    <PmCalendarFramePure
      :class="componentClass.element('frame')"
      :start-date="startDate"
      :end-date="endDate"
      :opening-hours="openingHours"
      :core-hours="coreHours"
      :public-holidays="publicHolidays"
    />

    <div :class="componentClass.element('control')">
      <slot name="control" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { differenceInCalendarDays } from 'date-fns'
import { useComponentClass } from '@thomasaull-shared/composables'

import { EVENT } from '@/constants/events'
import eventHub from '@/eventHub'

import PmCalendarFramePure, {
  type Props as CalendarFramePureProps,
} from '@/components/persoplan/PmCalendarFrame/PmCalendarFramePure.vue'

export interface Props
  extends Pick<
    CalendarFramePureProps,
    'publicHolidays' | 'openingHours' | 'coreHours'
  > {
  startDate: Date
  endDate: Date
  contentVisible?: boolean
  isGeneralEventsVisible?: boolean
  isCalendarEventsVisible?: boolean
  isBirthdaysVisible?: boolean
  isFreelancersVisible?: boolean
  isEmployeesVisible?: boolean
  isVehiclesVisible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  publicHolidays: () => [],
  contentVisible: true,
  isGeneralEventsVisible: true,
  isCalendarEventsVisible: true,
  isBirthdaysVisible: true,
  isFreelancersVisible: true,
  isEmployeesVisible: true,
  isVehiclesVisible: true,
})

export interface Emits {
  updateContainerHeight: [event: { id: string; height: number }]
}

const emit = defineEmits<Emits>()

const componentClass = useComponentClass({ props })
const elRoot = ref<HTMLElement>()
const elGeneralEvents = ref<HTMLElement>()
const elCalendarEvents = ref<HTMLElement>()
const elBirthdays = ref<HTMLElement>()
const elFreelancers = ref<HTMLElement>()
const elEmployees = ref<HTMLElement>()
const elVehicles = ref<HTMLElement>()
const elJobs = ref<HTMLElement>()
const elAfterJobs = ref<HTMLElement>()
const containerHeight = ref<number>()
let resizeObserver: ResizeObserver | undefined

const styles = computed(() => ({
  height: containerHeight.value ? `${containerHeight.value}px` : undefined,
}))

const numberOfDays = computed(
  () => differenceInCalendarDays(props.endDate, props.startDate) + 1
)

const stylesContent = computed(() => ({
  width: `calc(${numberOfDays.value} * var(--dayWidth))`,
}))

watch(
  () => props.contentVisible,
  (value, oldValue) => {
    /**
     * Save and restore container height, otherwise the user will be scrolled to the top
     */
    if (value === false && oldValue === true) {
      if (!elRoot.value) {
        throw new Error('elRoot is not filled')
      }

      containerHeight.value = elRoot.value.getBoundingClientRect().height
    }

    if (value === true && oldValue === false) {
      containerHeight.value = undefined
    }
  }
)

onMounted(() => {
  if (!elRoot.value) {
    throw new Error('elRoot is not filled')
  }

  const elements = [
    elRoot.value,
    elGeneralEvents.value,
    elCalendarEvents.value,
    elBirthdays.value,
    elFreelancers.value,
    elEmployees.value,
    elVehicles.value,
    elJobs.value,
    elAfterJobs.value,
  ]

  resizeObserver = new ResizeObserver(onResize)

  elements.forEach((element) => {
    if (!element) {
      throw new Error('calendar element is not filled')
    }

    resizeObserver?.observe(element)
  })
})

onBeforeUnmount(() => resizeObserver?.disconnect())

function onResize(entries: ResizeObserverEntry[]) {
  if (!props.contentVisible) return

  /**
   * Avoid ResizeObserver loop limit exceeded error
   * @see https://stackoverflow.com/a/58701523
   */
  window.requestAnimationFrame(() => {
    entries.forEach((entry) => updateContainerHeight(entry.target))
  })
}

function updateContainerHeight(element: Element) {
  if (element === elRoot.value) {
    eventHub.$emit(EVENT.UPDATE_SCROLLBAR_DIMENSIONS)
  }

  const id = element.getAttribute('id')
  if (!id) return

  emit('updateContainerHeight', {
    id,
    height: element.getBoundingClientRect().height,
  })
}
</script>

<style lang="scss">
.PmCalendarPure {
  $block: &;

  margin-left: var(--sidebarLeftWidthWithVisibility, 0);
  margin-right: var(--sidebarRightWidthWithVisibility, 0);
  position: relative;

  &-content {
    position: relative;
    z-index: 1;
    // padding-top: calc(var(--headerHeight) + var(--navbarHeight));
    padding-top: var(--PmCalendarPure-headerHeight);
    width: 100%;
    min-height: var(--sidebarHeight);

    // TODO:
    // height: 500vh;

    // overflow: hidden; // This prevents sticky elements inside to work
  }

  &-generalEvents {
    &.is-withContent {
      min-height: 41px;
    }
  }

  &-calendarEvents {
    &.is-withContent {
      min-height: 41px;
    }
  }

  &-birthdays {
    &.is-withContent {
      min-height: 41px;
    }
  }

  &-freelancers {
    &.is-withContent {
      padding-top: 41px;
    }
  }

  &-employees {
    &.is-withContent {
      padding-top: 41px;
    }
  }

  &-vehicles {
    &.is-withContent {
      padding-top: 41px;
    }
  }

  &-jobs {
    padding-top: 41px;
    min-height: var(--sidebarJobsHeight);
  }

  &-frame {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
  }

  &-settings {
    position: fixed;
    z-index: 2;
    top: var(--navigationHeight);
    left: 0;
    width: calc(var(--sidebarLeftWidth) + 1px);
    background-color: color.$gray-100;
    border-right: 1px solid color.$gray-100--alpha;
    // min-height: 40px;

    // Gradient
    &::after {
      content: '';
      width: 50px;
      position: absolute;
      left: 100%;
      top: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 10;
      background: linear-gradient(
          90deg,
          rgba(#000, 0.06),
          ease-out,
          rgba(#000, 0)
        ),
        linear-gradient(
          90deg,
          rgba(#000, 0.1),
          cubic-bezier(0, 0, 0, 1),
          rgba(#000, 0)
        );
    }
  }

  &-control {
    position: fixed;
    top: calc(var(--navigationHeight) + 4px);
    right: calc(var(--sidebarRightWidthWithVisibility) + 4px);
    z-index: 2;
  }
}
</style>
